import {
  Box,
  Button,
  FormattedRelativeTime,
  Heading,
  Link,
  List,
  ListItem,
  Panel,
  PanelHeader,
  PanelSection,
  PanelTitle,
  Paragraph,
  Strong,
  Table,
  TableTbody,
  TableTd,
  TableTh,
  TableThead,
  TableTr,
  Text,
} from '@customerio/pluma-components/react';
import { usePermissions } from '@customerio/hydra-permissions/react';
import { CopyableCode } from '@/components/copyable-code';
import { CredentialKeyPair } from '@/features/integrations';
import type { ApiCredential } from '@/services/db/api-credential/entity';
import { docsUrlWithRegion } from '@/utils/data-center';
import type { Region } from '@/utils/environment';
import { COPY_SOURCE, CREDENTIAL_TYPE } from '../analytics';

const AUTH_DOCS_URL = 'https://docs.customer.io/api/track/#section/Authentication';
const API_OVERVIEW_DOCS_URL = 'https://docs.customer.io/api/track/#section/Overview';
const CONTRIBUTED_LIBRARIES_DOCS_URL = 'https://docs.customer.io/journeys/libraries/#contributed-libraries';
const GETTING_STARTED_DOCS_URL = 'https://docs.customer.io/api/?api=journeys';

const API_CLIENTS = [
  { name: 'Ruby', href: 'https://github.com/customerio/customerio-ruby' },
  { name: 'Python', href: 'https://github.com/customerio/customerio-python' },
  { name: 'Go', href: 'https://github.com/customerio/go-customerio' },
] as const;

type CredentialsPanelProps = {
  credentials: readonly ApiCredential[];
  workspaceName: string;
  region: Region;
  apiCredentialsHref: string;
};

/**
 * The workspace's tracking credentials, plus the API-client links.
 *
 * One credential shows as a key pair; several show as a table, because the
 * snippet below can only be configured for one of them at a time.
 */
export function CredentialsPanel({ credentials, workspaceName, region, apiCredentialsHref }: CredentialsPanelProps) {
  const { hasPermission } = usePermissions();
  const canViewApiCredentials = hasPermission('account/api_credentials/view');
  const [onlyCredential] = credentials;

  return (
    <Panel>
      <PanelHeader>
        <PanelTitle>Credentials</PanelTitle>
      </PanelHeader>

      <PanelSection>
        <Paragraph>
          These keys can be used to{' '}
          <Link variant="secondary" href={docsUrlWithRegion(AUTH_DOCS_URL, region)} isExternal>
            authenticate with the API
          </Link>{' '}
          as well as configure the JavaScript snippet to send data to your <Strong>{workspaceName}</Strong> workspace
          (as shown below).
        </Paragraph>
      </PanelSection>

      <PanelSection>
        {credentials.length === 1 && onlyCredential != null ? (
          <CredentialKeyPair credential={onlyCredential} credentialType={CREDENTIAL_TYPE} source={COPY_SOURCE} />
        ) : (
          <CredentialsTable credentials={credentials} />
        )}

        {canViewApiCredentials ? (
          <Button variant="primary" mt="200" href={apiCredentialsHref}>
            Manage API Credentials
          </Button>
        ) : null}
      </PanelSection>

      <PanelSection withBorder>
        <Heading level="3" size="m" mb="200">
          API Clients
        </Heading>
        <Paragraph>
          Getting data into Customer.io should be easy. We have several supported API clients that you can use to get
          going quickly:
        </Paragraph>

        <List mb="250" gap="100">
          {API_CLIENTS.map((client) => (
            <ListItem key={client.name}>
              <Link href={client.href} variant="secondary" isExternal>
                {client.name}
              </Link>
            </ListItem>
          ))}
        </List>

        <Paragraph mb="200">
          Our awesome community contributed to{' '}
          <Link variant="secondary" isExternal href={docsUrlWithRegion(CONTRIBUTED_LIBRARIES_DOCS_URL, region)}>
            many more API libraries
          </Link>
          . And of course you can directly connect to our REST API if you prefer. Read more in our{' '}
          <Link variant="secondary" isExternal href={docsUrlWithRegion(API_OVERVIEW_DOCS_URL, region)}>
            API documentation
          </Link>
          .
        </Paragraph>

        <Paragraph>
          You can read more about how to get started in our{' '}
          <Link variant="secondary" isExternal href={docsUrlWithRegion(GETTING_STARTED_DOCS_URL, region)}>
            API documentation
          </Link>
          .
        </Paragraph>
      </PanelSection>
    </Panel>
  );
}

type CredentialsTableProps = {
  credentials: readonly ApiCredential[];
};

/**
 * `fs-block` on the value cells keeps the credentials out of FullStory session
 * recordings, as the key pair does.
 */
function CredentialsTable({ credentials }: CredentialsTableProps) {
  return (
    <Table caption="API credentials">
      <TableThead>
        <TableTr>
          <TableTh>Name</TableTh>
          <TableTh>Site ID</TableTh>
          <TableTh>API key</TableTh>
        </TableTr>
      </TableThead>
      <TableTbody>
        {credentials.map((credential) => (
          <TableTr key={credential.id}>
            <TableTd>
              {credential.name}
              <Text as="div" color="subtle">
                Created <FormattedRelativeTime date={credential.created_at} shouldUppercaseFirst={false} />
              </Text>
            </TableTd>
            <TableTd>
              <Box className="fs-block">
                <CopyableCode value={credential.site_id} idNamespace="integrations.credentialSiteId" truncate={false}>
                  {credential.site_id}
                </CopyableCode>
              </Box>
            </TableTd>
            <TableTd>
              <Box className="fs-block">
                <CopyableCode value={credential.api_key} idNamespace="integrations.credentialApiKey" truncate={false}>
                  {credential.api_key}
                </CopyableCode>
              </Box>
            </TableTd>
          </TableTr>
        ))}
      </TableTbody>
    </Table>
  );
}
